import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import type { RMODesempenho, AtividadeOficialRMO, PresencaOficialRMO } from "../../../../types/desempenho";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { ChevronDown, ChevronRight, Pencil, Check, X } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { relatorioMovimentoOficialService } from "../../../../services/relatorioMovimentoOficial";

interface RMOTabProps {
  data: RMODesempenho | null;
  onRefresh?: () => void;
}

const formatMin = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatDateBR = (dateStr: string) => {
  const parts = dateStr.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : dateStr;
};

const CORES = [
  "oklch(0.5 0.2 240)",
  "oklch(0.5 0.15 142)",
  "oklch(0.5 0.2 20)",
  "oklch(0.5 0.2 60)",
  "oklch(0.5 0.2 300)",
  "oklch(0.5 0.2 180)",
  "oklch(0.5 0.2 320)",
  "oklch(0.5 0.2 100)",
];

const CustomChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-card border border-border rounded-lg px-3 py-2 text-sm shadow-md" style={{ zIndex: 1000 }}>
        <p className="font-medium text-foreground mb-1">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} className="text-muted-foreground">
            {entry.name}: <span className="font-bold" style={{ color: entry.color }}>{formatMin(Number(entry.value))}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

interface ModalAtividadesProps {
  atividades: AtividadeOficialRMO[];
  data: string;
  militarNome: string;
  onClose: () => void;
}

function ModalAtividades({ atividades, data, militarNome, onClose }: ModalAtividadesProps) {
  const tipoLabel = (tipo: string | null) => {
    if (tipo === 'grupo') return 'Grupo';
    if (tipo === 'treinamento') return 'Treinamento';
    if (tipo === 'outros') return 'Outros';
    return tipo ?? '—';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="font-semibold">{militarNome}</p>
            <p className="text-sm text-muted-foreground">Atividades em {formatDateBR(data)}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-lg leading-none">✕</button>
        </div>
        <div className="overflow-y-auto p-4 space-y-2">
          {atividades.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Nenhuma atividade registrada.</p>
          ) : (
            atividades.map(at => (
              <div key={at.id} className="flex items-start justify-between gap-3 p-3 bg-secondary/30 rounded-lg text-sm">
                <div className="space-y-0.5">
                  <span className="inline-block bg-primary/10 text-primary rounded px-2 py-0.5 text-xs font-medium mr-2">
                    {tipoLabel(at.tipoAtividade)}
                  </span>
                  {at.grupoNome && <span className="font-medium">{at.grupoNome}</span>}
                  {at.descricaoAtividade && <p className="text-muted-foreground text-xs mt-1">{at.descricaoAtividade}</p>}
                </div>
                <span className="text-right font-bold shrink-0">{formatMin(at.duracaoMinutos)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function RMOTab({ data, onRefresh }: RMOTabProps) {
  const [expandedOficiais, setExpandedOficiais] = useState<Set<string>>(new Set());
  const [modalState, setModalState] = useState<{ atividades: AtividadeOficialRMO[]; data: string; nome: string } | null>(null);
  const [editDays, setEditDays] = useState<Set<string>>(new Set());
  const [editingRecord, setEditingRecord] = useState<string | null>(null);
  const [editInicio, setEditInicio] = useState("");
  const [editTermino, setEditTermino] = useState("");
  const [savingRecord, setSavingRecord] = useState(false);
  const [localPresenca, setLocalPresenca] = useState<PresencaOficialRMO[] | null>(null);

  const toDatetimeLocal = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const toggleOficial = (id: string) => {
    setExpandedOficiais(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleEditDay = (key: string) => {
    setEditDays(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setEditingRecord(null);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleStartEdit = (at: AtividadeOficialRMO) => {
    setEditingRecord(at.id);
    setEditInicio(toDatetimeLocal(at.inicio));
    setEditTermino(toDatetimeLocal(at.termino));
  };

  const handleCancelEdit = () => {
    setEditingRecord(null);
  };

  const handleSaveEdit = async (id: string) => {
    if (!editInicio) return;
    setSavingRecord(true);
    try {
      await relatorioMovimentoOficialService.ajustarHorariosRegistro(id, {
        data_hora_inicio: editInicio,
        data_hora_termino: editTermino || null,
      });
      setEditingRecord(null);

      // Atualizar localmente sem recarregar tudo
      const novoInicio = editInicio;
      const novoTermino = editTermino || null;
      const novaDuracao = novoTermino
        ? (new Date(novoTermino).getTime() - new Date(novoInicio).getTime()) / 60000
        : 0;

      const base = localPresenca ?? (data?.presencaOficiais ?? []);
      const updated: PresencaOficialRMO[] = base.map(oficial => ({
        ...oficial,
        porDia: oficial.porDia.map(dia => {
          const idx = dia.atividades.findIndex(a => a.id === id);
          if (idx === -1) return dia;
          const atAnterior = dia.atividades[idx];
          const diffDuracao = novaDuracao - atAnterior.duracaoMinutos;
          const novasAtividades = dia.atividades.map(a =>
            a.id === id
              ? { ...a, inicio: novoInicio, termino: novoTermino, duracaoMinutos: novaDuracao }
              : a
          );
          const novoMinutosAtivo = atAnterior.status === 'ativo'
            ? dia.minutosAtivo + diffDuracao
            : dia.minutosAtivo;
          const novoMinutosEmAtividade = atAnterior.status === 'em_atividade'
            ? dia.minutosEmAtividade + diffDuracao
            : dia.minutosEmAtividade;
          return { ...dia, atividades: novasAtividades, minutosAtivo: novoMinutosAtivo, minutosEmAtividade: novoMinutosEmAtividade };
        }),
        totalMinutosAtivo: oficial.porDia.reduce((acc, dia) => {
          const idx = dia.atividades.findIndex(a => a.id === id);
          if (idx === -1) return acc + dia.minutosAtivo;
          const at = dia.atividades[idx];
          const diff = at.status === 'ativo' ? novaDuracao - at.duracaoMinutos : 0;
          return acc + dia.minutosAtivo + diff;
        }, 0),
        totalMinutosEmAtividade: oficial.porDia.reduce((acc, dia) => {
          const idx = dia.atividades.findIndex(a => a.id === id);
          if (idx === -1) return acc + dia.minutosEmAtividade;
          const at = dia.atividades[idx];
          const diff = at.status === 'em_atividade' ? novaDuracao - at.duracaoMinutos : 0;
          return acc + dia.minutosEmAtividade + diff;
        }, 0),
      }));
      setLocalPresenca(updated);

      enqueueSnackbar("Horários ajustados com sucesso!", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao ajustar horários", { variant: "error" });
    } finally {
      setSavingRecord(false);
    }
  };

  if (!data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Nenhum dado disponível. Use os filtros acima para buscar informações.
        </CardContent>
      </Card>
    );
  }

  const presenca = localPresenca ?? (data.presencaOficiais ?? []);
  const nicks = presenca.map(o => o.militar.nome);

  // Zerar cache local quando data externa mudar (novo buscar)
  React.useEffect(() => { setLocalPresenca(null); }, [data]);

  // Gráfico: Tempo Ativo no QG por dia — X = data, séries = cada oficial
  const ativoChartData = useMemo(() => {
    const diasMap = new Map<string, Record<string, number>>();
    presenca.forEach(oficial => {
      oficial.porDia.forEach(dia => {
        if (!diasMap.has(dia.data)) diasMap.set(dia.data, {});
        diasMap.get(dia.data)![oficial.militar.nome] = Math.round(dia.minutosAtivo);
      });
    });
    return Array.from(diasMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([data, vals]) => ({ data: formatDateBR(data), ...vals }));
  }, [presenca]);

  // Gráfico: Tempo Em Atividade por dia — X = data, séries = cada oficial
  const atividadeChartData = useMemo(() => {
    const diasMap = new Map<string, Record<string, number>>();
    presenca.forEach(oficial => {
      oficial.porDia.forEach(dia => {
        if (!diasMap.has(dia.data)) diasMap.set(dia.data, {});
        diasMap.get(dia.data)![oficial.militar.nome] = Math.round(dia.minutosEmAtividade);
      });
    });
    return Array.from(diasMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([data, vals]) => ({ data: formatDateBR(data), ...vals }));
  }, [presenca]);

  return (
    <div className="space-y-6">
      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Responsáveis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.resumo.totalResponsaveis}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Tempo Médio Ativo por Dia</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatMin(data.resumo.tempoMedioAtivoPorDia)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos lado a lado */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Tempo Ativo no QG</CardTitle>
            <CardDescription>Minutos ativos por oficial por dia</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ativoChartData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="data" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                <Legend verticalAlign="top" wrapperStyle={{ fontSize: 11, paddingBottom: 8 }} />
                {nicks.map((nick, i) => (
                  <Bar key={nick} dataKey={nick} stackId="a" fill={CORES[i % CORES.length]} radius={i === nicks.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tempo Em Atividade</CardTitle>
            <CardDescription>Minutos em atividade por oficial por dia</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={atividadeChartData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="data" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                <Legend verticalAlign="top" wrapperStyle={{ fontSize: 11, paddingBottom: 8 }} />
                {nicks.map((nick, i) => (
                  <Bar key={nick} dataKey={nick} stackId="a" fill={CORES[i % CORES.length]} radius={i === nicks.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Presença Oficiais */}
      <Card>
        <CardHeader>
          <CardTitle>Presença Oficiais</CardTitle>
          <CardDescription>Oficiais com status ativo ou em atividade no período</CardDescription>
        </CardHeader>
        <CardContent>
          {presenca.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Nenhum registro encontrado.</p>
          ) : (
            <div className="space-y-1">
              {[...presenca]
                .sort((a, b) => b.totalMinutosAtivo - a.totalMinutosAtivo)
                .map((oficial, rankIdx) => {
                const isExpanded = expandedOficiais.has(oficial.militar.id);
                const totalGeral = oficial.totalMinutosAtivo + oficial.totalMinutosEmAtividade;
                return (
                  <div key={oficial.militar.id} className="border border-border rounded-lg overflow-hidden">
                    {/* Linha principal */}
                    <button
                      onClick={() => toggleOficial(oficial.militar.id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 bg-secondary/20 hover:bg-secondary/40 transition-colors text-left"
                    >
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                        : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                      }
                      <span className="text-xs text-muted-foreground w-5 shrink-0">{rankIdx + 1}</span>
                      <span className="font-medium flex-1">{oficial.militar.nome}</span>
                      <span className="text-xs text-muted-foreground">{oficial.militar.patente}</span>
                      <div className="flex gap-4 text-sm ml-4">
                        <span className="text-muted-foreground">Ativo: <span className="font-bold text-foreground">{formatMin(oficial.totalMinutosAtivo)}</span></span>
                        <span className="text-muted-foreground">Em Atividade: <span className="font-bold text-foreground">{formatMin(oficial.totalMinutosEmAtividade)}</span></span>
                        <span className="text-muted-foreground">Total: <span className="font-bold text-foreground">{formatMin(totalGeral)}</span></span>
                      </div>
                    </button>

                    {/* Expandido: dias */}
                    {isExpanded && (
                      <div className="divide-y divide-border/50">
                        {[...oficial.porDia].sort((a, b) => a.data.localeCompare(b.data)).map(dia => {
                          const editKey = `${oficial.militar.id}-${dia.data}`;
                          const isDayEditOpen = editDays.has(editKey);
                          return (
                            <div key={dia.data}>
                              <div className="flex items-center gap-4 px-8 py-2 text-sm hover:bg-secondary/10">
                                <span className="text-muted-foreground w-20 shrink-0">{formatDateBR(dia.data)}</span>
                                <span className="flex-1 text-muted-foreground">Ativo: <span className="font-medium text-foreground">{formatMin(dia.minutosAtivo)}</span></span>
                                {dia.minutosEmAtividade > 0 ? (
                                  <button
                                    onClick={() => setModalState({ atividades: dia.atividades.filter(a => a.status === 'em_atividade'), data: dia.data, nome: oficial.militar.nome })}
                                    className="text-primary hover:underline font-medium"
                                  >
                                    Em Atividade: {formatMin(dia.minutosEmAtividade)}
                                  </button>
                                ) : (
                                  <span className="text-muted-foreground">Em Atividade: <span className="font-medium text-foreground">—</span></span>
                                )}
                                <button
                                  onClick={() => toggleEditDay(editKey)}
                                  className={`ml-2 p-1 rounded transition-colors ${isDayEditOpen ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'}`}
                                  title="Ajustar horários"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              </div>

                              {isDayEditOpen && (
                                <div className="px-8 pb-3 pt-1 bg-secondary/5 space-y-1.5">
                                  <p className="text-xs text-muted-foreground font-medium mb-2">Registros do dia — ajuste os horários se necessário</p>
                                  {dia.atividades.map(at => (
                                    <div key={at.id} className="flex items-center gap-2 p-2 bg-card border border-border rounded-lg text-xs">
                                      <span className={`shrink-0 px-2 py-0.5 rounded font-medium ${at.status === 'em_atividade' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                        {at.status === 'em_atividade' ? 'Em Atividade' : 'Ativo'}
                                      </span>
                                      {at.grupoNome && <span className="text-muted-foreground shrink-0">{at.grupoNome}</span>}
                                      {editingRecord === at.id ? (
                                        <>
                                          <input
                                            type="datetime-local"
                                            value={editInicio}
                                            onChange={e => setEditInicio(e.target.value)}
                                            className="flex-1 min-w-0 rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                                          />
                                          <span className="text-muted-foreground shrink-0">→</span>
                                          <input
                                            type="datetime-local"
                                            value={editTermino}
                                            onChange={e => setEditTermino(e.target.value)}
                                            className="flex-1 min-w-0 rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                                          />
                                          <button
                                            onClick={() => handleSaveEdit(at.id)}
                                            disabled={savingRecord}
                                            className="shrink-0 p-1 rounded bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors"
                                            title="Salvar"
                                          >
                                            <Check className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={handleCancelEdit}
                                            disabled={savingRecord}
                                            className="shrink-0 p-1 rounded hover:bg-secondary/60 text-muted-foreground transition-colors"
                                            title="Cancelar"
                                          >
                                            <X className="w-3.5 h-3.5" />
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <span className="text-muted-foreground flex-1">
                                            {new Date(at.inicio).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                            {' → '}
                                            {at.termino
                                              ? new Date(at.termino).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                                              : <span className="text-amber-500 font-medium">em aberto</span>
                                            }
                                          </span>
                                          <span className="font-bold text-foreground shrink-0">{formatMin(at.duracaoMinutos)}</span>
                                          <button
                                            onClick={() => handleStartEdit(at)}
                                            className="shrink-0 p-1 rounded hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors"
                                            title="Editar horários"
                                          >
                                            <Pencil className="w-3 h-3" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Responsáveis RMO */}
      <Card>
        <CardHeader>
          <CardTitle>Responsáveis RMO</CardTitle>
          <CardDescription>
            Desempenho dos responsáveis por relatório de movimento oficial
          </CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-xs">
                <th className="text-left pb-2 w-8">#</th>
                <th className="text-left pb-2">Militar</th>
                <th className="text-left pb-2">Patente</th>
                <th className="text-right pb-2">Tempo Ativo</th>
                <th className="text-right pb-2">Dias Ativos</th>
              </tr>
            </thead>
            <tbody>
              {[...data.responsaveis]
                .sort((a, b) => b.tempoTotalAtivo - a.tempoTotalAtivo)
                .map((responsavel, index) => (
                <tr key={responsavel.militar.id} className="border-b border-border/50 last:border-0 hover:bg-secondary/20 transition-colors">
                  <td className="py-2 text-muted-foreground text-xs">{index + 1}</td>
                  <td className="py-2 font-medium">{responsavel.militar.nome}</td>
                  <td className="py-2 text-muted-foreground">{responsavel.militar.patente}</td>
                  <td className="py-2 text-right font-bold">{formatMin(responsavel.tempoTotalAtivo)}</td>
                  <td className="py-2 text-right">{responsavel.totalDias}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Modal Atividades */}
      {modalState && (
        <ModalAtividades
          atividades={modalState.atividades}
          data={modalState.data}
          militarNome={modalState.nome}
          onClose={() => setModalState(null)}
        />
      )}
    </div>
  );
}

